import type { CompanyInfo, QuoteData } from "./pdf-generator";
import { withQuotedCostSplit } from "@workspace/api-zod/quote-cost-split";
import { readPdfTemplateSnapshot } from "@workspace/api-zod/pdf-template";

const SHARED_SCOPE_NAME = "__shared__";

interface ExportOptions {
  quote: any;
  profile?: any;
  market: string;
  isMultiScope: boolean;
  activeScope: string;
  scopeNames: string[];
}

export function buildQuoteExportInputs({
  quote,
  profile,
  market,
  isMultiScope,
  activeScope,
  scopeNames,
}: ExportOptions): { quoteData: QuoteData; company?: CompanyInfo } {
  const allItems = quote.lineItems.map(withQuotedCostSplit);
  const filtered = !isMultiScope || activeScope === "__combined__"
    ? allItems
    : allItems.filter((item: any) =>
        item.scopeName === activeScope || item.scopeName === SHARED_SCOPE_NAME);
  const isFiltered = isMultiScope && activeScope !== "__combined__";
  const subtotal = isFiltered
    ? filtered.filter((item: any) => !item.isOptional).reduce((sum: number, item: any) => sum + Number(item.subtotal || 0), 0)
    : Number(quote.subtotal || 0);
  const storedSubtotal = Number(quote.subtotal || 0);
  const scaleStoredAmount = (amount: unknown) => {
    const storedAmount = Number(amount || 0);
    return storedSubtotal > 0 ? subtotal * (storedAmount / storedSubtotal) : 0;
  };
  const overhead = isFiltered ? scaleStoredAmount(quote.overheadAmount) : Number(quote.overheadAmount || 0);
  const profit = isFiltered ? scaleStoredAmount(quote.profitAmount) : Number(quote.profitAmount || 0);
  const contingency = isFiltered ? scaleStoredAmount(quote.contingencyAmount) : Number(quote.contingencyAmount || 0);
  const total = isFiltered ? subtotal + overhead + profit + contingency : Number(quote.totalPrice || 0);
  const vat = isFiltered
    ? (quote.includeVat ? total * Number(quote.vatRate || 0) / 100 : 0)
    : Number(quote.vatAmount || 0);
  const grandTotal = isFiltered ? total + vat : Number(quote.grandTotal || 0);

  const quoteData: QuoteData = {
    pdfTemplate: readPdfTemplateSnapshot(quote.pdfTemplate),
    headingNames: quote.headingNames ?? null,
    quoteNumber: quote.quoteNumber,
    status: quote.status,
    projectAddress: quote.projectAddress,
    projectType: !isMultiScope
      ? quote.projectType
      : activeScope === "__combined__"
        ? scopeNames.join(" and ") || quote.projectType
        : activeScope,
    description: quote.description,
    subtotal: subtotal.toFixed(2),
    overheadAmount: overhead.toFixed(2),
    profitAmount: profit.toFixed(2),
    contingencyAmount: contingency.toFixed(2),
    totalPrice: total.toFixed(2),
    vatRate: quote.vatRate,
    vatAmount: vat.toFixed(2),
    grandTotal: grandTotal.toFixed(2),
    includeVat: quote.includeVat,
    showCostBreakdown: quote.showCostBreakdown,
    showMarkupBreakdown: quote.showMarkupBreakdown !== false,
    showItemDescriptions: quote.showItemDescriptions !== false,
    showQuantityUnits: quote.showQuantityUnits !== false,
    showLineItems: quote.showLineItems !== false,
    validUntil: quote.validUntil,
    assumptions: quote.assumptions,
    exclusions: quote.exclusions,
    termsConditions: quote.termsConditions ?? profile?.defaultTermsConditions,
    paymentTerms: quote.paymentTerms ?? profile?.defaultPaymentTerms,
    projectTimeline: quote.projectTimeline,
    commencementNote: quote.commencementNote,
    createdAt: quote.createdAt,
    confidenceLevel: quote.confidenceLevel,
    summaryMode: quote.summaryMode || false,
    scopeLabel: isFiltered ? activeScope : null,
    isMultiScope: isMultiScope && !isFiltered,
    scopeNames: isMultiScope && !isFiltered ? scopeNames : [],
    lineItems: filtered.map((item: any) => ({
      section: item.section || "MAIN WORKS",
      scopeName: item.scopeName ?? null,
      category: item.category,
      name: item.name,
      description: item.description,
      customerDescription: item.customerDescription,
      inclusions: item.inclusions || [],
      exclusions: item.exclusions || [],
      quantity: item.quantity,
      unit: item.unit,
      unitCost: item.unitCost,
      labourCost: item.labourCost,
      materialCost: item.materialCost,
      plantCost: item.plantCost,
      subtotal: item.subtotal,
      isOptional: item.isOptional || false,
      sortOrder: item.sortOrder ?? null,
      displayStage: item.displayStage ?? null,
      displaySubGroup: item.displaySubGroup ?? null,
    })),
    client: quote.client ? {
      name: quote.client.name,
      email: quote.client.email || undefined,
      phone: quote.client.phone || undefined,
      address: quote.client.address || undefined,
      postcode: quote.client.postcode || undefined,
    } : null,
  };

  const company = profile ? {
    name: profile.companyName || "Your Company",
    address: profile.address || undefined,
    phone: profile.phone || undefined,
    email: profile.email || undefined,
    website: profile.website || undefined,
    vatNumber: profile.vatNumber || undefined,
    logoUrl: profile.logoUrl || undefined,
    showCompanyName: profile.showCompanyName ?? true,
    logoSize: profile.logoSize || "medium",
    market,
  } satisfies CompanyInfo : undefined;

  return { quoteData, company };
}